import { Service } from '@genial/services';
import {
  OptoutBySearchTermSuccessListResponse,
  OptoutByCustomerIdSuccessResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { selectorFamily } from 'recoil';

export interface CustomersListResponseNormalized
  extends Utils.ArrayElement<NonNullable<OptoutBySearchTermSuccessListResponse['data']>> {}

export const customersLockListQuery = selectorFamily<
  | {
      data: CustomersListResponseNormalized[];
    }
  | undefined,
  { searchTerm: string }
>({
  key: 'BlockList/BlockList',
  get:
    ({ searchTerm }) =>
    async () => {
      if (!searchTerm) {
        return undefined;
      }

      const customers = await Service.Customer.V1.getOptoutBySearchTerm({
        screenName: 'block-list',
        searchTerm,
      });

      const normalizedCustomers: CustomersListResponseNormalized[] | null | undefined =
        customers.data?.map((customer) => ({
          ...customer,
        })) ?? [];

      return {
        data: normalizedCustomers,
      };
    },
});

export interface IHistoryNormalized {
  info: {
    label: string;
    value: string;
  }[];
  title: string;
  iconName: string;
  iconColor: string;
  iconSize: string;
  iconBackground: string;
}

export interface BlockDetailsNormalized
  extends NonNullable<OptoutByCustomerIdSuccessResponse['data']> {
  historyNormalized?: IHistoryNormalized[];
}

export const blockHistoryDetails = selectorFamily<
  | {
      data: BlockDetailsNormalized;
    }
  | undefined,
  { customerId: number; channel: string }
>({
  key: 'BlockList/BlockDetails',
  get:
    ({ customerId, channel }) =>
    async () => {
      if (!customerId || !channel) {
        return undefined;
      }

      const blockDetails = await Service.Customer.V1.getOptoutByCustomerId({
        screenName: 'block-details',
        customerId,
        channel,
      });

      const historyNormalized = blockDetails?.data?.history?.map((item) => {
        const defineStatus = () => {
          const labelStatus = {
            labelDate: '',
            labelChannel: '',
            labelResponsable: '',
          };

          if (item.status === 'unlock') {
            labelStatus.labelDate = 'Data de desbloqueio:';
            labelStatus.labelChannel = 'Canal:';
            labelStatus.labelResponsable = 'Responsável:';
          } else if (item.status === 'request-unlock') {
            labelStatus.labelDate = 'Data de solicitação:';
            labelStatus.labelChannel = 'Canal de envio:';
            labelStatus.labelResponsable = 'Solicitante:';
          } else if (item.status === 'lock') {
            labelStatus.labelDate = 'Data de bloqueio:';
            labelStatus.labelChannel = 'Canal:';
            labelStatus.labelResponsable = 'Responsável:';
          }

          return labelStatus;
        };

        const labelStatus = defineStatus();

        const info = [
          {
            label: labelStatus.labelDate,
            value: item.infoDateFormatted || '',
          },
          {
            label: labelStatus.labelChannel,
            value: item.channel || '',
          },
          {
            label: labelStatus.labelResponsable,
            value: item.responsable || '',
          },
        ];

        if (item.responsableName?.length) {
          info.push({
            label: 'Nome solicitante:',
            value: item.responsableName || '',
          });
        }

        if (item.status === 'request-unlock') {
          if (item.observation?.length) {
            info.push({
              label: 'Informação adicional:',
              value: item.observation,
            });
          }
        }

        return {
          info,
          title: item.title || '',
          iconName: item.statusIcon ? item.statusIcon : '',
          iconColor: item.statusColor ? item.statusColor : '',
          iconSize: item.statusSize ? item.statusSize : '',
          iconBackground: item.statusBackground ? item.statusBackground : '',
        };
      });

      const blockDetailsNormalized: BlockDetailsNormalized = {
        ...blockDetails.data,
        historyNormalized: historyNormalized || [],
      };

      return {
        data: blockDetailsNormalized,
      };
    },
});
